// ─────────────────────────────────────────────────────────────────────────────
// useUsersData — server-side fetch of the user directory (search/filter/paginate
// all happen on the API) plus the row mutations (status change, remove). Exposes
// { users, total, teams, loading, error, refetch, changeStatus, remove }.
// Mirrors Nodes' useNodesData, adapted for the users endpoint's server paging.
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useState } from "react";
import { getUsers, updateUserStatus, deleteUser } from "../../../services/usersService";

const PAGE_SIZE = 20;

export function useUsersData({ search, filterStatus, filterRole, filterTeam, page }) {
  const [users,   setUsers]   = useState([]);
  const [total,   setTotal]   = useState(0);
  const [teams,   setTeams]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const refetch = useCallback(async (opts = {}) => {
    setLoading(true);
    try {
      const data = await getUsers({
        search: search || undefined,
        status: filterStatus !== "All" ? filterStatus : undefined,
        role:   filterRole   !== "All" ? filterRole   : undefined,
        team:   filterTeam   !== "All" ? filterTeam   : undefined,
        page,
        limit:  PAGE_SIZE,
        ...opts,
      });
      if (data) {
        setUsers(data.users ?? []);
        setTotal(data.total ?? 0);
        // Accumulate the team list across pages for the Team filter.
        setTeams((prev) => {
          const set = new Set(prev);
          (data.users ?? []).forEach((u) => u.team && set.add(u.team));
          return ["All", ...[...set].filter((t) => t !== "All").sort()];
        });
      }
    } catch (err) {
      console.warn("[Users] fetch failed:", err?.message);
    } finally {
      setLoading(false);
    }
  }, [search, filterStatus, filterRole, filterTeam, page]);

  useEffect(() => { refetch(); }, [refetch]);

  // ── Row mutations (optimistic) ─────────────────────────────────────────────
  const changeStatus = useCallback(async (id, status) => {
    setError(null);
    try {
      await updateUserStatus(id, status);
      setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, status } : u)));
    } catch (err) {
      setError(err.message || "Failed to update user status.");
    }
  }, []);

  const remove = useCallback(async (id) => {
    setError(null);
    try {
      await deleteUser(id);
      setUsers((prev) => prev.filter((u) => u.id !== id));
      setTotal((t) => t - 1);
    } catch (err) {
      setError(err.message || "Failed to remove user.");
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return {
    users, total, teams, loading,
    error, clearError,
    refetch, changeStatus, remove,
    pageSize: PAGE_SIZE,
  };
}
